import React, { useEffect, useState } from 'react'
import { Combobox } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import useDebounce from '@/util/hook/useDebounce.tsx'
import useQuerySearchDevices, {
    DEVICE_SEARCH_MIN_QUERY_LENGTH,
    IDeviceSearchItem
} from '@/api/device/searchDevices.ts'
import { describeLastSeen } from '@/util/date.ts'

const SEARCH_DEBOUNCE_MS = 300

const normalizeSerial = (text: string): string => text.replace(/[\s-]/g, '')

export interface IProps {
    value: string
    onChange: (serial: string) => void
    initialSerial?: string
    disabled?: boolean
    /** 이미 저장된 연결을 끊을 때 호출된다. 없으면 화면에서만 비운다. */
    onDisconnect?: () => Promise<void>
}

const DeviceSerialCombobox: React.FC<IProps> = ({
    value,
    onChange,
    initialSerial = '',
    disabled = false,
    onDisconnect
}) => {
    const { t } = useTranslation()
    const [query, setQuery] = useState('')
    const [selectedItem, setSelectedItem] = useState<IDeviceSearchItem | null>(null)
    const [isDisconnecting, setDisconnecting] = useState(false)

    const normalizedQuery = normalizeSerial(query)
    const debouncedQuery = useDebounce(normalizedQuery, SEARCH_DEBOUNCE_MS)
    const isSearching = debouncedQuery.length >= DEVICE_SEARCH_MIN_QUERY_LENGTH

    const { data, isFetching } = useQuerySearchDevices(
        isSearching ? debouncedQuery : '',
        !disabled && !value
    )

    // 전체 시리얼을 붙여넣거나 스캔한 경우 목록을 거치지 않고 바로 확정한다.
    useEffect(() => {
        if (value || !isSearching) return
        // 디바운스가 아직 이전 검색어를 들고 있으면 확정하지 않는다.
        if (normalizedQuery !== debouncedQuery) return

        const exactMatch = data?.devices?.find(
            (device) => device.deviceSerial === debouncedQuery && !device.inUse
        )
        if (exactMatch) {
            setSelectedItem(exactMatch)
            onChange(exactMatch.deviceSerial)
        }
    }, [data, debouncedQuery, normalizedQuery, isSearching, value, onChange])

    const onSelect = (device: IDeviceSearchItem | null) => {
        if (!device) return
        setSelectedItem(device)
        setQuery('')
        onChange(device.deviceSerial)
    }

    const clearSelection = () => {
        setSelectedItem(null)
        setQuery('')
        onChange('')
    }

    const onClickDisconnect = async () => {
        const isSavedConnection = !!initialSerial && value === initialSerial
        if (!isSavedConnection || !onDisconnect) {
            clearSelection()
            return
        }

        const confirmed = window.confirm(
            t('account.protectee.device.disconnect.confirm', { serial: value })
        )
        if (!confirmed) return

        setDisconnecting(true)
        try {
            await onDisconnect()
            clearSelection()
        } finally {
            setDisconnecting(false)
        }
    }

    if (value) {
        const lastSeen = selectedItem
            ? describeLastSeen(selectedItem.statusUpdatedAt, selectedItem.isOnline)
            : { key: 'account.protectee.device.connected', values: undefined }

        return (
            <div className={'flex items-center gap-[8px] bg-sub_b px-[12px] py-[10px]'}>
                <span className={'text-[16px] font-medium text-black truncate'}>{value}</span>
                <span className={'text-[12px] text-gray3 whitespace-nowrap'}>
                    {selectedItem?.isOnline && '● '}
                    {t(lastSeen.key, lastSeen.values)}
                </span>
                <button
                    type={'button'}
                    className={'ml-auto shrink-0 text-[12px] text-sub_r underline'}
                    disabled={disabled || isDisconnecting}
                    onClick={onClickDisconnect}
                >
                    {isDisconnecting
                        ? t('account.protectee.device.disconnect.loading')
                        : t('account.protectee.device.disconnect')}
                </button>
            </div>
        )
    }

    const devices = data?.devices ?? []
    const isQueryTooShort =
        normalizedQuery.length > 0 && normalizedQuery.length < DEVICE_SEARCH_MIN_QUERY_LENGTH

    return (
        <Combobox value={selectedItem} onChange={onSelect} disabled={disabled} nullable>
            <div className={'relative'}>
                <Combobox.Input
                    className={
                        'w-full bg-sub_b px-[12px] py-[10px] text-[16px] font-medium text-black border-none'
                    }
                    placeholder={t('account.protectee.device.search.placeholder')}
                    displayValue={() => ''}
                    autoComplete={'off'}
                    onChange={(event) => setQuery(event.target.value)}
                />

                <Combobox.Options
                    className={
                        'absolute z-10 mt-[4px] w-full max-h-[240px] overflow-y-auto bg-white border border-gray2 rounded-[5px] shadow-lg'
                    }
                >
                    {isQueryTooShort && (
                        <p className={'px-[12px] py-[10px] text-[13px] text-gray3'}>
                            {t('account.protectee.device.search.minLength', {
                                n: DEVICE_SEARCH_MIN_QUERY_LENGTH
                            })}
                        </p>
                    )}

                    {!isQueryTooShort && isFetching && devices.length === 0 && (
                        <p className={'px-[12px] py-[10px] text-[13px] text-gray3'}>
                            {t('account.protectee.device.search.loading')}
                        </p>
                    )}

                    {!isQueryTooShort && !isFetching && devices.length === 0 && (
                        <p className={'px-[12px] py-[10px] text-[13px] text-gray3'}>
                            {t('account.protectee.device.search.empty')}
                        </p>
                    )}

                    {!isQueryTooShort &&
                        devices.map((device) => {
                            const lastSeen = describeLastSeen(
                                device.statusUpdatedAt,
                                device.isOnline
                            )
                            return (
                                <Combobox.Option
                                    key={device.deviceSerial}
                                    value={device}
                                    disabled={device.inUse}
                                    className={({ active }) =>
                                        `flex items-center gap-[8px] px-[12px] py-[10px] text-[14px] ${
                                            device.inUse
                                                ? 'text-gray3 cursor-not-allowed'
                                                : 'cursor-pointer text-black'
                                        } ${active && !device.inUse ? 'bg-sub_b' : ''}`
                                    }
                                >
                                    <span className={'font-medium truncate'}>
                                        {device.deviceSerial}
                                    </span>
                                    <span
                                        className={
                                            'ml-auto shrink-0 text-[12px] text-gray3 whitespace-nowrap'
                                        }
                                    >
                                        {device.inUse
                                            ? t('account.protectee.device.inUse')
                                            : `${device.isOnline ? '● ' : ''}${t(
                                                  lastSeen.key,
                                                  lastSeen.values
                                              )}`}
                                    </span>
                                </Combobox.Option>
                            )
                        })}

                    {data?.hasMore && (
                        <p
                            className={
                                'px-[12px] py-[8px] text-[12px] text-gray3 border-t border-gray2'
                            }
                        >
                            {t('account.protectee.device.search.hasMore')}
                        </p>
                    )}
                </Combobox.Options>
            </div>
        </Combobox>
    )
}

export default DeviceSerialCombobox
